import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

// Module-level singleton — one socket connection shared across all hook consumers
let globalSocket = null;
let globalRefCount = 0;

function getSocket() {
  if (!globalSocket) {
    const token = localStorage.getItem("cb_token");
    globalSocket = io(BACKEND_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
    });
  }
  return globalSocket;
}

export default function useSocket() {
  const [connected, setConnected] = useState(() => globalSocket?.connected ?? false);
  const socketRef = useRef(null);

  useEffect(() => {
    const s = getSocket();
    socketRef.current = s;
    globalRefCount++;

    // Socket may already be connected (e.g. another component created it)
    if (s.connected) setConnected(true);

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);

    return () => {
      globalRefCount--;
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);

      // Tear down global socket when the last consumer unmounts
      if (globalRefCount <= 0 && globalSocket) {
        globalSocket.disconnect();
        globalSocket = null;
      }
    };
  }, []);

  // If the socket is not yet connected, wait for the connect event
  // before emitting.  This prevents join events from being silently lost.
  const emitWhenReady = (event, data) => {
    const s = socketRef.current;
    if (!s) return;
    if (s.connected) {
      s.emit(event, data);
    } else {
      s.once("connect", () => s.emit(event, data));
    }
  };

  const joinChat = (chatId) => emitWhenReady("chat:join", { chatId });
  const leaveChat = (chatId) => socketRef.current?.emit("chat:leave", { chatId });
  const joinPlay = (sessionId) => emitWhenReady("play:join", { sessionId });
  const leavePlay = (sessionId) => socketRef.current?.emit("play:leave", { sessionId });

  return { socket: socketRef.current, connected, joinChat, leaveChat, joinPlay, leavePlay };
}
